import { useAppDispatch, useAppSelector } from 'appState/store'
import { LoadingAnimation } from '@outbound-ai/ursa'
import { authApi } from 'features/auth/authApi'
import {
  ascEnabled,
  eobDeliveryWindow,
  eobEnabled,
  eobFaxNumber,
  logoutTime,
  setAuthSession,
  setLoginDetailsUpdated,
  updateAuthCurrentCustomer,
  updateAuthCurrentTenant,
} from 'features/auth/authSlice'
import { updateDownloadNotifier } from 'features/claims/claimsSlice'
import { CSCreateQuery, CSRestQuery, Filter } from 'features/claims/claimTypes'
import {
  getUserPreference,
  getUserProfile,
  settingsServiceApiPaths,
  setUserPreference,
} from 'features/settings/settingsApi'
import { useUserProfile } from 'features/settings/settingsHooks'
import { IUserProfile, UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import { useRevalidateAllSwrCache } from 'hooks/useRevalidateAllSwrCache'
import { PathsEnum } from 'pages/Paths'
import { useEffect, useMemo, useState } from 'react'
import { FaBuilding } from 'react-icons/fa'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  IMultiLevelDropdown,
  ISelectedMenu,
  MultiLevelDropdown,
} from 'components/DropdownMultiLevel/DropdownMultiLevel'
import { IDropdownLabel, ISelectedMenuItem } from 'components/DropdownMultiLevel/MenuItems'
import styled from 'styled-components'
import { getMinValue, longTextToSmall } from 'utils/helper'
import { notificator } from 'utils/notificator/notificator'
import storage, { StorageEnum } from 'utils/storage'
import { httpTenantsService } from 'utils/http'
import { apiPaths, RelativeRootEnum } from 'configs/apiConfigs'
import { getTntIdFromStorage, getTntIdFromUrl, setTntIdInStorage, setTenantIdInStorage } from 'utils/tenantUrlUtils'
import { SELECTED_TENANT_UNAVAILABLE, isManualTenantSwitch, setManualTenantSwitch } from 'utils/tenantUtils'

export { SELECTED_TENANT_UNAVAILABLE } from 'utils/tenantUtils'
export const TENANT_LIMIT = {
  Min: 20,
  Total: 40,
}
interface Tenant {
  label: string
  value: string // This is tenantId (GUID)
  tntId?: number // This is tntId (integer) - for URLs
  customerId: string
  customerName: string
}

interface ICustomerSupportedFeatures {
  supportedFeatures?: {
    asc?: boolean
  }
}

const tenantLabel: IDropdownLabel = {
  title: 'Select customer',
  searchPlaceholder_1: 'Search customers',
  searchPlaceholder_2: 'Search Practices',
}

export const TenantSelection = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { isConnected, isHumanAgentCallInProgress } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)

  const { user, hasUpdatedLoginDetails, currentCustomer } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)

  const [selectedTenant, setSelectedTenant] = useState<Tenant>()

  const [isUserPreferenceEmpty, setIsUserPreferenceEmpty] = useState(false)

  const [isTenantLoading, setIsTenantLoading] = useState(false)

  const revalidateAllSwrCache = useRevalidateAllSwrCache()

  const hasMultipleTenants = profile && profile?.tenants.length > 1

  const updateLoginDetailsOnTenantLoad = async () => {
    if (user?.id) {
      try {
        const url = apiPaths.loginDetails(user.id)
        await httpTenantsService.patch(url, {})
      } catch (error) {
        console.error('Failed to update login details after user login:', error)
      }
    }
  }

  function getMappedCustomerList(profile: IUserProfile | undefined) {
    if (!profile || !profile?.tenants) {
      navigate(PathsEnum.home)
      return
    }

    /**
     * Get the list of customers Id and remove the duplicates
     */
    const customerIds = profile.tenants.map((tenant) => tenant.customerId)
    const removeDuplicateIds = [...new Set(customerIds)]

    /**
     * API returns customer inside tenants. so we are mapping the data based on Multi level dropdown
     */
    const customers = removeDuplicateIds.map((id) => {
      const tenantList = profile.tenants.filter((tenant) => tenant.customerId === id)
      const { customerName, customerId } = tenantList[0].customer // Each tenant has same customer so we are taking the first one

      const groupedTenants = tenantList.map((t) => {
        return {
          value: t.description,
          id: t.tenantId, // Keep using tenantId for dropdown ID
          tntId: t.tntId, // Store tntId for URL navigation
        }
      })

      return {
        value: customerName,
        id: customerId,
        items: groupedTenants,
      } as IMultiLevelDropdown
    })
    customers.forEach((obj) => obj.items.sort((a, b) => a.value.localeCompare(b.value)))
    customers.sort((a, b) => a.value.localeCompare(b.value))
    return customers
  }

  const tenantOptions = useMemo(
    () =>
      profile?.tenants.map((tenant) => ({
        label: tenant.description,
        value: tenant.tenantId, // tenantId (GUID) for headers
        tntId: tenant.tntId, // tntId (integer) for URLs
        customerId: tenant.customerId,
        customerName: tenant.customer.customerName,
        eobFaxNumber: tenant.eobFaxNumber ? tenant.eobFaxNumber : '',
      })) || [],
    [profile],
  )

  useEffect(() => {
    const currentPreferredTenant = sessionStorage.getItem(UserPreferencesKeysEnum.outboundAiPreferredTenant)
    const currentPreferredTntId = getTntIdFromStorage()
    const currentPreferredCustomer = sessionStorage.getItem(UserPreferencesKeysEnum.outboundAiPreferredCustomer)
    const currentPathTntId = getTntIdFromUrl()
    const manualSwitchInProgress =
      isManualTenantSwitch() && !!currentPreferredTntId && currentPathTntId !== currentPreferredTntId

    // Clear selection mode only when a manual switch is not in-flight.
    // If current URL tenant differs from preferred tenant, keep manual mode so post-save navigation can complete.
    if (!manualSwitchInProgress) {
      localStorage.removeItem('tenant_selection_mode')
    }

    if (currentPreferredTenant && currentPreferredTntId) {
      const preferredTenantObj = {
        label: '',
        value: currentPreferredTenant,
        tntId: parseInt(currentPreferredTntId),
        customerId: currentPreferredCustomer,
        customerName: '',
      } as Tenant

      setSelectedTenant(preferredTenantObj)
      return // Don't check preferences if we already have both values
    }

    // Check user preference only if we don't have both values and tenantOptions are loaded
    if ((!currentPreferredTenant || !currentPreferredTntId) && tenantOptions.length > 0) {
      // Need to fetch from backend and look up tntId from profile
      checkUserPreference()
    }
  }, [tenantOptions])

  useEffect(() => {
    if (selectedTenant && Object.keys(selectedTenant).length === 0 && tenantOptions.length > 0) {
      const minTenantValue = getMinValue(tenantOptions)
      const foundTenant = tenantOptions.find((tenant) => tenant.value === minTenantValue)
      if (foundTenant) {
        dispatch(updateAuthCurrentTenant(foundTenant?.label))
        dispatch(updateAuthCurrentCustomer(foundTenant?.customerId))
        dispatch(eobFaxNumber(foundTenant?.eobFaxNumber))
        getEobDetails()
        setSelectedTenant(foundTenant)
        saveUserPreferenceTenant(foundTenant)
      }
    }

    if (selectedTenant?.value) {
      const foundTenant = tenantOptions.find((tenant) => tenant.value === selectedTenant.value)

      if (foundTenant) {
        setSelectedTenant(foundTenant)
        dispatch(updateAuthCurrentTenant(foundTenant?.label))
        dispatch(updateAuthCurrentCustomer(foundTenant?.customerId))
        dispatch(eobFaxNumber(foundTenant?.eobFaxNumber))
        getEobDetails()
      } else {
        setDefaultTenant(tenantOptions)
      }
    }
  }, [profile, selectedTenant, tenantOptions])

  // Update login details only once when user first logs in
  useEffect(() => {
    if (user?.id && !hasUpdatedLoginDetails) {
      updateLoginDetailsOnTenantLoad()
      dispatch(setLoginDetailsUpdated(true))
    }
  }, [user?.id, hasUpdatedLoginDetails, dispatch])

  // Clear login details flag when user logs out
  useEffect(() => {
    if (!user?.id) {
      dispatch(setLoginDetailsUpdated(false))
    }
  }, [user?.id, dispatch])

  // Load customer level supported features (e.g. Appeal Status Check) when the customer changes
  useEffect(() => {
    if (currentCustomer) {
      getCustomerSupportedFeatures(currentCustomer)
    }
  }, [currentCustomer])

  // Sync tenant selection with URL changes (when user pastes a URL with different tenant)
  useEffect(() => {
    const currentPreferredTenant = sessionStorage.getItem(UserPreferencesKeysEnum.outboundAiPreferredTenant)
    const currentPreferredCustomer = sessionStorage.getItem(UserPreferencesKeysEnum.outboundAiPreferredCustomer)

    // Only update if the tenant in storage differs from the current selectedTenant
    if (currentPreferredTenant && currentPreferredTenant !== selectedTenant?.value) {
      const preferredTenantObj = {
        label: '',
        value: currentPreferredTenant,
        customerId: currentPreferredCustomer,
        customerName: '',
      } as Tenant

      setSelectedTenant(preferredTenantObj)
    }
  }, [location.pathname])

  const getEobDetails = () => {
    getUserPreference(settingsServiceApiPaths.userPreferencesGet(UserPreferencesKeysEnum.eob_enabled)).then((res) => {
      if (res && res.value) {
        dispatch(eobEnabled(res.value === 'true' ? true : false))
      } else {
        dispatch(eobEnabled(false))
      }
    })
    getUserPreference(
      settingsServiceApiPaths.userPreferencesGet(UserPreferencesKeysEnum.eob_max_delivery_timeout_hrs),
    ).then((res) => {
      if (res && res.value) {
        dispatch(eobDeliveryWindow(res.value))
      } else {
        dispatch(eobDeliveryWindow('24'))
      }
    })
    getUserPreference(settingsServiceApiPaths.userPreferencesGet(UserPreferencesKeysEnum.logout_time)).then((res) => {
      if (res && res.value) {
        const logouttime = res.value
        if (logouttime) {
          dispatch(logoutTime(res.value))
        } else {
          dispatch(logoutTime('0'))
        }
      }
    })
  }

  const getCustomerSupportedFeatures = (customerId: string) => {
    const customerQuery = new CSRestQuery(99998, 0)
    customerQuery.addFilter(new Filter('customerid', '=', customerId))
    const url = apiPaths.csSearch(RelativeRootEnum.cppuCustomers, new CSCreateQuery(customerQuery))

    httpTenantsService
      .get(url)
      .then((res) => {
        const customer: ICustomerSupportedFeatures | undefined = res.data?.data?.[0]
        dispatch(ascEnabled(Boolean(customer?.supportedFeatures?.asc)))
      })
      .catch((error) => {
        console.error('Failed to fetch customer supported features:', error)
        dispatch(ascEnabled(false))
      })
  }

  async function checkUserPreference() {
    const url = settingsServiceApiPaths.userPreferencesGet(UserPreferencesKeysEnum.outboundAiPreferredTenant)
    const preferenceData = await getUserPreference(url)

    if (Object.keys(preferenceData).length === 0) {
      setIsUserPreferenceEmpty(true)
      return
    }

    // We got tenantId (GUID) from preferences, now we need to find the tntId
    const preferredTenantId = preferenceData.value

    // Look up the full tenant object from profile to get tntId
    const matchingTenant = tenantOptions.find((t) => t.value === preferredTenantId)

    if (matchingTenant) {
      // Store both tntId and tenantId
      sessionStorage.setItem(UserPreferencesKeysEnum.outboundAiPreferredTenant, matchingTenant.value)
      sessionStorage.setItem(
        UserPreferencesKeysEnum.outboundAiPreferredCustomer,
        preferenceData.customerId || matchingTenant.customerId,
      )

      // CRITICAL: Store both values using utility functions
      setTenantIdInStorage(matchingTenant.value) // tenantId (GUID)
      if (matchingTenant.tntId) {
        setTntIdInStorage(matchingTenant.tntId) // tntId (integer)
      }

      setSelectedTenant(matchingTenant)

      // Force a navigation now that we have tntId
      const currentPath = window.location.pathname
      // Navigate if:
      // 1. Currently at home/login/root
      // 2. URL contains literal :tntId placeholder
      // 3. URL is at noaccess-found (user got stuck there)
      const shouldNavigate =
        currentPath === PathsEnum.home ||
        currentPath === PathsEnum.login ||
        currentPath === '/' ||
        currentPath.includes(':tntId') ||
        currentPath.includes('/noaccess-found')

      if (shouldNavigate) {
        window.location.href = `/${matchingTenant.tntId}/claims`
      }
    } else {
      // Preferred tenant not found in user's tenant list - fall back to default
      setIsUserPreferenceEmpty(true)
      await setDefaultTenant(tenantOptions)
    }
  }

  async function setDefaultTenant(tenantOptions: Tenant[]) {
    const tenantValue = getMinValue(tenantOptions)!
    const defaultTenant = tenantOptions.find((tenant) => tenant.value === tenantValue)

    if (defaultTenant) {
      if (!isUserPreferenceEmpty) {
        await saveUserPreferenceTenant(defaultTenant, 'Selected practice not available, changing selected practice')
      } else {
        await saveUserPreferenceTenant(defaultTenant)
      }
    }
  }

  const handleSelectedTenant = (val: ISelectedMenuItem) => {
    const { prev, current } = val
    if (current?.items?.length > 0) {
      return
    }

    const preparedTenant = {
      customerId: prev.id,
      value: current.id, // tenantId (GUID)
      tntId: current.tntId, // tntId (integer)
    } as Tenant

    // Save tenant selection mode as manual when user changes tenant
    setManualTenantSwitch()

    // Check if we're on a claim-specific route
    const currentPath = window.location.pathname
    const pathParts = currentPath.split('/')
    const pathWithoutTntId = pathParts.length > 2 ? '/' + pathParts.slice(2).join('/') : '/claims'
    const isClaimSpecificRoute =
      pathWithoutTntId.includes('/claims/claim/') ||
      pathWithoutTntId.includes('/calls-summary/') ||
      pathWithoutTntId.includes('/claim/')

    // For claim-specific routes, do immediate lightweight switch
    if (isClaimSpecificRoute && preparedTenant.tntId) {
      // Store tenant info synchronously
      setTntIdInStorage(preparedTenant.tntId)
      setTenantIdInStorage(preparedTenant.value)
      sessionStorage.setItem(UserPreferencesKeysEnum.outboundAiPreferredTenant, preparedTenant.value)
      sessionStorage.setItem(UserPreferencesKeysEnum.outboundAiPreferredCustomer, preparedTenant.customerId)

      // Update backend in background (non-blocking)
      Promise.all([
        setUserPreference(settingsServiceApiPaths.userPreferencesPut(), {
          key: UserPreferencesKeysEnum.outboundAiPreferredTenant,
          value: preparedTenant.value,
          customerId: preparedTenant.customerId,
        }),
        user?.id ? httpTenantsService.patch(apiPaths.loginDetails(user.id)) : Promise.resolve(),
      ]).catch((err) => console.error('Background tenant update failed:', err))

      // Single navigation - browser will handle the reload
      window.location.href = `/${preparedTenant.tntId}/claims`
      return
    }

    // For other routes, use normal flow
    handleTenantChange(preparedTenant)
  }

  const handleTenantChange = async (tenant: Tenant) => {
    setIsTenantLoading(true)

    const userRoot = user?.id ? settingsServiceApiPaths.user(user?.id) : undefined

    const userProfile = userRoot ? await getUserProfile(userRoot) : undefined

    const latestTenants = userProfile?.tenants.map(
      (t) =>
        ({
          label: t.description,
          value: t.tenantId, // tenantId (GUID)
          tntId: t.tntId, // tntId (integer)
          customerId: t.customerId,
          customerName: t.customer.customerName,
        } as Tenant),
    )

    const isSelectedTenantExists = latestTenants?.find((t) => t.value === tenant.value)

    if (isSelectedTenantExists && Object.keys(isSelectedTenantExists).length !== 0) {
      await saveUserPreferenceTenant(tenant)
      dispatch(updateDownloadNotifier(false))
    } else {
      if (latestTenants) {
        const tenantValue = getMinValue(latestTenants)!
        const defaultTenant = latestTenants.find((tenant) => tenant.value === tenantValue)

        if (defaultTenant) {
          await saveUserPreferenceTenant(defaultTenant, 'Selected practice not available, changing selected practice')
        }
      }
    }
  }

  async function saveUserPreferenceTenant(tenant: Tenant, isTenantUnAvailable?: string | undefined) {
    try {
      const userColumnState = {
        key: UserPreferencesKeysEnum.outboundAiPreferredTenant,
        value: tenant.value,
        customerId: tenant.customerId,
      }
      setUserPreference(settingsServiceApiPaths.userPreferencesPut(), userColumnState)

      // CRITICAL: Store both tntId (for URLs) and tenantId (for headers)
      sessionStorage.setItem(UserPreferencesKeysEnum.outboundAiPreferredTenant, tenant.value)
      sessionStorage.setItem(UserPreferencesKeysEnum.outboundAiPreferredCustomer, tenant.customerId)

      // Store both using the new utility functions
      setTenantIdInStorage(tenant.value) // tenantId (GUID) for headers
      if (tenant.tntId) {
        setTntIdInStorage(tenant.tntId) // tntId (integer) for URLs
      }

      const checkAuthSession = async () => {
        const authToken = await authApi.getAuthToken()

        if (authToken) {
          const currentUser = storage.get(StorageEnum.currentUser)
          dispatch(setAuthSession({ currentUser }))
        }
      }

      await checkAuthSession()
      await revalidateAllSwrCache()

      // Update login details when tenant is changed
      if (user?.id) {
        try {
          const url = apiPaths.loginDetails(user.id)
          await httpTenantsService.patch(url)
          // Reset the flag so login details can be updated again on next login
          dispatch(setLoginDetailsUpdated(false))
        } catch (error) {
          console.error('Failed to update login details after tenant change:', error)
        }
      }

      if (isTenantUnAvailable) {
        sessionStorage.setItem(SELECTED_TENANT_UNAVAILABLE, isTenantUnAvailable)
      }

      // Only navigate if we're doing a manual tenant switch (not just syncing from URL)
      const shouldNavigateAfterSave = isManualTenantSwitch()

      if (shouldNavigateAfterSave) {
        handleClick(tenant.tntId)
      }
      setIsTenantLoading(false)
    } catch (error: any) {
      console.log('🚀 ~ file: TenantSelection.tsx:127 ~ handleTenantChange ~ error:', error)
      notificator.notify({
        heading: 'Failed to update practice',
        type: 'error',
      })
      //notificator.error('Failed to update Practice')
      setIsTenantLoading(false)
    }
  }

  const handleClick = (targetTntId?: number | string) => {
    // Use provided tntId if available, otherwise get from storage
    // When manually switching, we pass the target tntId directly to ensure accuracy
    const newTntId = targetTntId ? String(targetTntId) : getTntIdFromStorage()

    if (newTntId) {
      const currentPath = window.location.pathname
      const pathParts = currentPath.split('/')
      // Remove empty first element and tenant ID (first two elements)
      const pathWithoutTntId = pathParts.length > 2 ? '/' + pathParts.slice(2).join('/') : '/claims'

      // Check if current path is claim-specific (claim detail, call summary, etc.)
      // For these routes, redirect to claims listing instead of preserving path
      // because the claim ID likely doesn't exist in the new tenant
      const isClaimSpecificRoute =
        pathWithoutTntId.includes('/claims/claim/') ||
        pathWithoutTntId.includes('/calls-summary/') ||
        pathWithoutTntId.includes('/claim/')

      const targetPath = isClaimSpecificRoute ? '/claims' : pathWithoutTntId

      // Build new URL with new tntId and appropriate path
      window.location.href = `/${newTntId}${targetPath}`
    } else {
      // Fallback: Navigate to home to trigger tenant selection if tntId not available
      console.error('[TenantSelection] No tntId available for navigation')
      window.location.href = PathsEnum.home
    }
  }
  const isConnectedToolTip =
    isConnected || isHumanAgentCallInProgress
      ? 'You cannot switch practices while a Get a Rep call is in progress.'
      : hasMultipleTenants
      ? 'Click to change your selection'
      : ''

  const toolTip = (
    <>
      <span>
        Viewing {longTextToSmall(selectedTenant?.customerName, TENANT_LIMIT?.Min, TENANT_LIMIT?.Total)}/
        {longTextToSmall(selectedTenant?.label, TENANT_LIMIT?.Min, TENANT_LIMIT?.Total)}
      </span>
      <br />
      <span>{isConnectedToolTip}</span>
    </>
  )

  const showDropdown = selectedTenant && Object.keys(selectedTenant).length !== 0 && selectedTenant.label
  const disableDropdown = isConnected || isHumanAgentCallInProgress || tenantOptions.length <= 1

  return (
    <>
      {isTenantLoading ? (
        <LoadingContainer>
          <div className="loader-content">
            <span>Changing selected practice</span>
            <LoadingAnimation />
          </div>
        </LoadingContainer>
      ) : (
        ''
      )}

      {showDropdown && (
        <Div onlyOneTenant={tenantOptions.length <= 1}>
          <MultiLevelDropdown
            labelCount={TENANT_LIMIT}
            data={getMappedCustomerList(profile)!}
            selectedMenu={getSelectedTenant(selectedTenant)}
            changeMenuItem={(e: any) => handleSelectedTenant(e)}
            icon={<FaBuilding className="icon-building" />}
            toolTipMessage={toolTip}
            disabled={disableDropdown}
            dropdownLabel={tenantLabel}
          />
        </Div>
      )}
    </>
  )
}

function getSelectedTenant(selectedTenant: Tenant) {
  return {
    menu: selectedTenant.customerName,
    item: selectedTenant.label,
    activeId: selectedTenant.value,
  } as ISelectedMenu
}

const Div = styled.div<{ onlyOneTenant: boolean }>`
  .custom-multi-level::after {
    display: ${({ onlyOneTenant }) => (onlyOneTenant ? 'none' : 'inline-block')};
  }
  position: relative;
`

const LoadingContainer = styled.div`
  background-color: rgb(121 121 121 / 85%);
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: calc(calc(calc(100vh - 0rem) - 0rem) - 0rem);
  z-index: 9999;
  .loader-content {
    display: flex;
    flex-direction: column;
    height: 70%;
    align-items: center;
    justify-content: center;
    color: #fff;
    span {
      font-size: 1rem;
      font-weight: 700;
    }
    small {
      margin-bottom: 0.625rem;
      font-size: 0.875rem;
    }
  }
`
